import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { LogOut, X } from "lucide-react";
import { API_BASE, api } from "../api";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function SettingsDrawer({ open, onClose }: Props) {
  const { data: identity, isLoading } = useQuery({
    queryKey: ["identity"],
    queryFn: api.getIdentity,
    enabled: open,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-ink-950/70 backdrop-blur-[2px] z-40 animate-fade-in"
        onClick={onClose}
      />
      <aside className="fixed top-0 right-0 bottom-0 w-full sm:w-[420px] bg-ink-900 border-l border-ink-700 z-50 overflow-y-auto animate-fade-in">
        <div className="p-6 border-b border-ink-800 flex items-center justify-between">
          <div>
            <div className="term-label mb-1">environment</div>
            <h2 className="font-serif text-2xl text-ink-100">settings</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-ink-400 hover:text-phosphor-300"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <section>
            <div className="term-label mb-2">backend url</div>
            <code className="block text-xs text-phosphor-300 bg-ink-850 border border-ink-700 px-3 py-2 break-all">
              {API_BASE}
            </code>
          </section>

          <section>
            <div className="term-label mb-2">identity</div>
            <div className="bg-ink-850 border border-ink-700 p-4 space-y-2">
              {isLoading ? (
                <div className="text-xs text-ink-400">resolving…</div>
              ) : identity ? (
                <>
                  {identity.email && (
                    <div className="flex justify-between text-xs">
                      <span className="text-ink-400">email</span>
                      <span className="text-ink-100">{identity.email}</span>
                    </div>
                  )}
                  {identity.name && (
                    <div className="flex justify-between text-xs">
                      <span className="text-ink-400">name</span>
                      <span className="text-ink-100">{identity.name}</span>
                    </div>
                  )}
                  {identity.user_uuid && (
                    <div className="flex justify-between text-xs gap-4">
                      <span className="text-ink-400">uuid</span>
                      <span
                        className="text-ink-300 truncate"
                        title={identity.user_uuid}
                      >
                        {identity.user_uuid.slice(0, 12)}…
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-xs text-ink-400">
                  unauthenticated <span className="text-ink-500">(dev mode?)</span>
                </div>
              )}
            </div>
          </section>

          <section>
            <div className="term-label mb-2">about</div>
            <p className="text-xs leading-relaxed text-ink-300">
              simdb spins up ephemeral Postgres sandboxes seeded from a
              natural-language prompt. Every database is short-lived and
              destroyed on schedule. Think{" "}
              <em className="font-serif text-phosphor-300">e2b, but for databases.</em>
            </p>
          </section>

          <section className="pt-4 border-t border-ink-800">
            <a
              href="/cdn-cgi/access/logout"
              className="btn btn-danger w-full justify-center"
            >
              <LogOut size={14} />
              sign out
            </a>
          </section>
        </div>
      </aside>
    </>
  );
}
